import { useState } from "react";
import { AlarmClock, CheckCircle2, ClipboardList, Plus, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { capi, errMsg, fday, ftime } from "./chatApi";
import { LinkChip } from "./LinkChip";

/* Snooze late items until 9 AM IST tomorrow (My pending + the daily reminder message). */
export const SnoozeBtn = ({ ids, label = "Snooze till 9 AM", onDone, testid }) => {
  const [busy, setBusy] = useState(false);
  const go = async (e) => {
    e.stopPropagation(); setBusy(true);
    try { const r = await capi("post", "/chat/pending/snooze", { message_ids: ids }); toast.success(`Snoozed ${r.count} item(s) till 9 AM tomorrow`); onDone?.(); window.dispatchEvent(new Event("chat-pending-changed")); }
    catch (er) { toast.error(errMsg(er)); } finally { setBusy(false); }
  };
  return <button type="button" onClick={go} disabled={busy || !ids?.length} className="inline-flex items-center gap-1 rounded-md border border-amber-300 bg-amber-50 px-2 py-0.5 text-[10px] font-semibold text-amber-800 hover:bg-amber-100 disabled:opacity-50" data-testid={testid}>
    <AlarmClock className="h-3 w-3" />{label}</button>;
};

const OUTCOMES = ["Resolved – process as is", "Replace product", "Remove product", "Change qty", "Cancel parcel"];

/* Close the linked task / query right from My pending (parcel queries need an outcome). */
export const DoneBtn = ({ x, onDone }) => {
  const [busy, setBusy] = useState(false);
  const run = async (fn, msg) => {
    setBusy(true);
    try { await fn(); toast.success(msg); onDone?.(); window.dispatchEvent(new Event("chat-pending-changed")); }
    catch (er) { toast.error(errMsg(er)); } finally { setBusy(false); }
  };
  const stop = (e) => e.stopPropagation();
  const cls = "mt-1.5 inline-flex items-center gap-1 rounded-md border border-emerald-300 bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-800 hover:bg-emerald-100 disabled:opacity-50";
  if (x.link.kind === "parcel_query") return (
    <select defaultValue="" disabled={busy} onClick={stop} onChange={(e) => { const o = e.target.value; e.target.value = ""; if (o) run(() => capi("post", `/order-register/queries/${x.link.id}/outcome`, { outcome: o }), "Query solved"); }}
      className={cls} data-testid={`chat-pending-done-${x.message_id}`}>
      <option value="">Mark solved as…</option>{OUTCOMES.map((o) => <option key={o} value={o}>{o}</option>)}</select>);
  return <button type="button" disabled={busy} onClick={(e) => { stop(e); run(() => capi("post", `/chat/pending/${x.message_id}/done`), x.link.kind === "task" ? "Task done" : "Query solved"); }}
    className={cls} data-testid={`chat-pending-done-${x.message_id}`}><CheckCircle2 className="h-3 w-3" />Mark done</button>;
};

/* "My pending": open tasks / queries linked in my chats — late ones first; tap jumps to the message. */
export const PendingPanel = ({ data, onJump, onClose, onChanged }) => (
  <aside className="flex w-full flex-col border-l bg-white md:w-96" data-testid="chat-pending-panel">
    <header className="flex items-center gap-2 border-b px-4 py-3"><ClipboardList className="h-4 w-4 text-amber-600" />
      <b className="text-sm">My pending ({data?.count || 0})</b>
      <button onClick={onClose} className="ml-auto rounded p-1 hover:bg-slate-100" data-testid="chat-pending-close"><X className="h-4 w-4" /></button></header>
    <div className="flex-1 space-y-2 overflow-y-auto p-3">
      {!data?.items?.length && <p className="mt-10 text-center text-sm text-slate-400">Nothing pending — all linked tasks and queries are done.</p>}
      {data?.items?.map((x) => (
        <div key={x.message_id} role="button" tabIndex={0} onClick={() => onJump(x)} className={`w-full cursor-pointer rounded-xl border p-2.5 text-left text-xs transition-colors hover:border-indigo-300 hover:bg-indigo-50/40 ${x.link.late ? "border-rose-200 bg-rose-50/50" : ""}`}
          data-testid={`chat-pending-item-${x.message_id}`}>
          <div className="mb-1 flex items-center gap-1 text-[10px] font-semibold text-slate-500"><span className="rounded bg-slate-100 px-1.5 py-0.5">#{x.channel_name}</span>
            <span>{x.sender_name}</span><span className="ml-auto">{fday(x.created_at)} {ftime(x.created_at)}</span></div>
          {x.text && <p className="mb-1 line-clamp-2 text-slate-700">{x.text}</p>}
          <div className="flex flex-wrap items-center gap-1.5"><LinkChip l={x.link} onOpen={() => onJump(x)} />
            <DoneBtn x={x} onDone={onChanged} />
            {x.link.late && <SnoozeBtn ids={[x.message_id]} onDone={onChanged} testid={`chat-pending-snooze-${x.message_id}`} />}</div>
        </div>))}
    </div>
  </aside>
);

/* Admin: joint channels shared by the teams you pick (e.g. Processing + Account). */
export const JointDialog = ({ teams, joints, onClose, onDone }) => {
  const [name, setName] = useState("");
  const [pick, setPick] = useState([]);
  const save = async () => {
    try { await capi("post", "/chat/joint", { name, teams: pick }); toast.success("Joint channel created"); setName(""); setPick([]); onDone(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const del = async (j) => {
    if (!window.confirm(`Delete joint channel ${j.name}? Its messages stay saved but nobody can open it.`)) return;
    try { await capi("delete", `/chat/joint/${j.id.slice(6)}`); onDone(j.id); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="chat-joint-dialog">
        <DialogHeader><DialogTitle>Joint channels</DialogTitle></DialogHeader>
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Channel name, e.g. Processing + Account" className="w-full rounded-lg border px-3 py-2 text-sm" data-testid="chat-joint-name" />
        <div className="flex flex-wrap gap-1.5">{teams.map((t) => (
          <button key={t} onClick={() => setPick(pick.includes(t) ? pick.filter((x) => x !== t) : [...pick, t])}
            className={`rounded-full border px-2.5 py-1 text-xs ${pick.includes(t) ? "border-indigo-500 bg-indigo-600 text-white" : "hover:bg-slate-50"}`} data-testid={`chat-joint-team-${t.replace(/\s+/g, "-")}`}>{t}</button>))}</div>
        <button onClick={save} disabled={!name.trim() || pick.length < 2} className="inline-flex items-center justify-center gap-1 rounded-lg bg-indigo-600 px-3 py-2 text-sm font-semibold text-white disabled:opacity-40" data-testid="chat-joint-save">
          <Plus className="h-4 w-4" />Create channel</button>
        <div className="space-y-1 border-t pt-2">{joints.map((j) => (
          <div key={j.id} className="flex items-center gap-2 text-sm"><b className="flex-1 truncate">{j.name}</b><span className="truncate text-[11px] text-slate-500">{j.teams.join(" · ")}</span>
            <button onClick={() => del(j)} className="text-rose-500" data-testid={`chat-joint-del-${j.id}`}><Trash2 className="h-4 w-4" /></button></div>))}
          {!joints.length && <p className="text-xs text-slate-400">No joint channels yet.</p>}</div>
      </DialogContent>
    </Dialog>
  );
};
